/**
 * How far the table has moved between skeleton and loaded content.
 *
 * `swapping` is the intermediate phase: skeleton columns over loaded rows.
 * Its mirror — loaded columns over skeleton rows — is deliberately not a phase,
 * because a consumer's `valueGetter` would run against a row that has none of
 * the fields it dereferences. Skeleton columns read no row data, so they are
 * safe over either kind.
 *
 * Distinct from the `loading` prop, which says which halves the *caller* is
 * still waiting for. This says what the grid currently has.
 *
 * This exists because AG-grid does not provide a mechanism to atomically swap both
 * rows and columns.
 */
export const LOADING_PHASES = ["skeleton", "swapping", "loaded"] as const;

export type LoadingPhase = (typeof LOADING_PHASES)[number];

/**
 * One phase toward `target`, so exactly one of (rows, columns) changes per
 * commit. AG Grid applies `columnDefs` and `rowData` separately whichever way
 * they are set, so changing both in one commit lets a loaded column meet a
 * skeleton row.
 */
export function stepToward(
  current: LoadingPhase,
  target: LoadingPhase,
): LoadingPhase {
  const from = LOADING_PHASES.indexOf(current);
  const to = LOADING_PHASES.indexOf(target);
  if (from === to) {
    return current;
  }
  return LOADING_PHASES[from + (to > from ? 1 : -1)];
}
